import { Edit as EditIcon, Visibility as VisibilityIcon, AttachFile as AttachFileIcon } from "@mui/icons-material";
import { ListItemIcon, ListItemText, Menu, MenuItem, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LoadingComp } from "../../../shared/components/loading/Loading";
import { useDebounce, useToast } from "../../../shared/hooks";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import { CapitalizationService } from "../../../shared/services/api/capitalization/CapitalizationService";
import { CapitalizationData } from "../../../shared/services/api/capitalization/types";
import { CapitalizationToolbar } from "./CapitalizationToolbar";
import ModalCapitalizationDetails from "./Modals/ModalCapitalizationDetails";
import { ModalCapitalizationAttachments } from "./Modals/ModalCapitalizationAttachments";
import { TableCapitalization } from "./TableCapitalization";
import { GenericFollowUpModalContent } from "../../client/components/ModalFollowUpGeneric/GenericFollowUpModalContent";
import { getFollowUpServiceByContext } from "../../client/components/ModalFollowUpGeneric/FollowUpService";

import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { PermissionRolesEnum, PermissionActionsEnum } from "../../../shared/enums/profiles";
import { useAuthContext } from "../../../context";

export const CapitalizationPresentation: React.FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { debounce } = useDebounce();
	const toastHandler = useToast();
	const theme = useTheme();
	const navigate = useNavigate();
	const { loggedUser } = useAuthContext();

	const followUpServices = getFollowUpServiceByContext("capitalization");

	// Estados dos filtros (Local State)
	const [textSearch, setTextSearch] = useState("");
	const [dataIni, setDataIni] = useState("");
	const [dataFim, setDataFim] = useState("");
	const [status, setStatus] = useState("todos");
	const [clientId, setClientId] = useState("");

	// Paginação
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [totalCount, setTotalCount] = useState(0);

	// Dados e Loading
	const [rows, setRows] = useState<CapitalizationData[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [refreshTable, setRefreshTable] = useState(false);

	// Menu State
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [selectedRow, setSelectedRow] = useState<CapitalizationData | null>(null);
	const [openDetailsModal, setOpenDetailsModal] = useState(false);
	const [openAttachmentsModal, setOpenAttachmentsModal] = useState(false);
	const [openFollowUpModal, setOpenFollowUpModal] = useState(false);

	const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, row: CapitalizationData, type?: "menu" | "followup") => {
		if (type === "followup") {
			setSelectedRow(row);
			setOpenFollowUpModal(true);
		} else {
			setAnchorEl(event.currentTarget);
			setSelectedRow(row);
		}
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
		setSelectedRow(null);
	};

	const handleOpenDetails = () => {
		setOpenDetailsModal(true);
		setAnchorEl(null); // Close menu
	};

	const handleCloseDetails = () => {
		setOpenDetailsModal(false);
		handleCloseMenu();
	};

	const handleOpenAttachments = () => {
		setOpenAttachmentsModal(true);
		setAnchorEl(null);
	};

	const handleCloseAttachments = () => {
		setOpenAttachmentsModal(false);
		handleCloseMenu();
	};

	const handleCloseFollowUp = () => {
		setOpenFollowUpModal(false);
		setSelectedRow(null);
	};



	const handleEdit = () => {
		if (selectedRow) {
			navigate(`/capitalizacao/editar/${selectedRow.id}`);
			handleCloseMenu();
		}
	};

	const handleRefreshTable = () => {
		setRefreshTable((prev) => !prev);
	};

	const handleFetchData = (currentPage: number, limit: number, search: string, ini: string, fim: string, stat: string, client: string) => {
		setIsLoading(true);
		debounce(() => {
			CapitalizationService.getCapitalizationAll(currentPage + 1, limit, search, ini, fim, stat, client)
				.then((result) => {
					setIsLoading(false);
					if (result instanceof Error) {
						// Em caso de erro (ou 304 sem dados), define como vazio para não travar a tela
						setRows([]);
						setTotalCount(0);
					} else {
						setRows(result.data);
						setTotalCount(result.total);
					}
				});
		});
	};

	// Sync URL Params -> Local State & Fetch
	useEffect(() => {
		const paramText = searchParams.get("search") ?? "";
		const paramDataIni = searchParams.get("dataIni") ?? "";
		const paramDataFim = searchParams.get("dataFim") ?? "";
		const paramStatus = searchParams.get("status") ?? "todos";
		const paramClientId = searchParams.get("clientId") ?? "";
		const paramPage = Number(searchParams.get("pagina") || "1") - 1; // URL is 1-based, State is 0-based
		const paramRowsPerPage = Number(searchParams.get("pageSize") || "10");

		setTextSearch(paramText);
		setDataIni(paramDataIni);
		setDataFim(paramDataFim);
		setStatus(paramStatus);
		setClientId(paramClientId);
		setPage(paramPage >= 0 ? paramPage : 0);
		setRowsPerPage(paramRowsPerPage);

		handleFetchData(paramPage >= 0 ? paramPage : 0, paramRowsPerPage, paramText, paramDataIni, paramDataFim, paramStatus, paramClientId);
	}, [searchParams, refreshTable]);

	const handleChangePage = (newPage: number) => {
		setPage(newPage);
		const params: any = {
			pagina: String(newPage + 1),
			pageSize: String(rowsPerPage),
			search: textSearch,
			status,
			dataIni,
			dataFim,
			clientId
		};
		setSearchParams(params, { replace: true });
	};

	const handleChangeRowsPerPage = (newRowsPerPage: number) => {
		setRowsPerPage(newRowsPerPage);
		setPage(0);
		const params: any = {
			pagina: "1",
			pageSize: String(newRowsPerPage),
			search: textSearch,
			status,
			dataIni,
			dataFim,
			clientId
		};
		setSearchParams(params, { replace: true });
	};

	const handleSearch = () => {
		setPage(0);
		const params: any = {
			pagina: "1",
			pageSize: String(rowsPerPage),
			search: textSearch,
			status,
			dataIni,
			dataFim,
			clientId
		};
		setSearchParams(params, { replace: true });
	};

	const handleClientChange = (newClientId: string | null) => {
		const clientVal = newClientId || "";
		setClientId(clientVal);
	};

	const handleNew = () => {
		navigate('/capitalizacao/novo');
	};

	return (
		<LayoutBaseDePagina
			titulo="Capitalização"
			barraDeFerramentas={
				<CapitalizationToolbar
					textSearch={textSearch}
					onChangeTextSearch={setTextSearch}
					onClearTextSearch={() => setTextSearch('')}
					onClickRefresh={handleSearch}

					dateStart={dataIni}
					onChangeDateStart={setDataIni}
					dateEnd={dataFim}
					onChangeDateEnd={setDataFim}

					status={status}
					onChangeStatus={setStatus}

					showClientSelect={true}
					onChangeClient={handleClientChange}

					showStatusSelect={true}
					showDateRange={true}
					showRefreshButton={true}

					showButtonNew={true}
					onClickNew={handleNew}
				/>
			}
		>

			<LoadingComp isopen={isLoading} />

			<TableCapitalization
				rows={rows}
				page={page}
				rowsPerPage={rowsPerPage}
				totalCount={totalCount}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				onOpenMenu={handleOpenMenu}
			/>

			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleCloseMenu}
			>
				<MenuItem onClick={handleOpenDetails}>
					<ListItemIcon>
						<VisibilityIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText>Detalhes</ListItemText>
				</MenuItem>

				{checkByProfileGuard(PermissionRolesEnum.CapitalizationOptions, PermissionActionsEnum.Update) && (
					<MenuItem onClick={handleEdit}>
						<ListItemIcon>
							<EditIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText>Editar</ListItemText>
					</MenuItem>
				)}

				{checkByProfileGuard(PermissionRolesEnum.CapitalizationOptions, PermissionActionsEnum.AttachFiles) && (
					<MenuItem onClick={handleOpenAttachments}>
						<ListItemIcon>
							<AttachFileIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText>Anexos</ListItemText>
					</MenuItem>
				)}
			</Menu>

			<ModalCapitalizationDetails
				open={openDetailsModal}
				onClose={handleCloseDetails}
				capitalizationId={selectedRow?.id || ""}
			/>

			<ModalCapitalizationAttachments
				open={openAttachmentsModal}
				onClose={handleCloseAttachments}
				capitalizationId={selectedRow?.id || ""}
			/>

			{openFollowUpModal && selectedRow && loggedUser && (
				<GenericFollowUpModalContent
					open={openFollowUpModal}
					onClose={handleCloseFollowUp}
					clientId={selectedRow.id}
					loggedUser={loggedUser}
					services={followUpServices}
					permissionSend={true}
					permissionAddContact={true}
				/>
			)}
		</LayoutBaseDePagina>
	);
};
